import {
  Box, Button, FormControl, FormLabel, Input, NumberInput, NumberInputField,
  VStack, useColorModeValue, Stack, Center, Avatar, Text, Badge, Heading, IconButton,
  Alert, AlertIcon, Tooltip, ButtonGroup, FormErrorMessage, FormHelperText
} from "@chakra-ui/react";
import QrScanner from "react-qr-scanner";
import { useAddPointToUserMutation } from "../../slices/adminApiSlice";
import Loader from "../../components/Loader";
import { useSelector } from "react-redux";
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { ToastService } from '../../services/toast/toastService';
import { useGetUserDataMutation } from "../../slices/userApiSlice";
import { FaQrcode, FaTimes } from "react-icons/fa";

const AdminAddPoints = () => {
const { adminInfo } = useSelector(state => state.adminAuth);
const [addPointUser, { isLoading }] = useAddPointToUserMutation();
const { success, error } = ToastService();
const [getUserData] = useGetUserDataMutation();
const [userData, setUserData] = useState(null);
const [isQrScannerVisible, setQrScannerVisible] = useState(false);
const [errorMessage, setErrorMessage] = useState('');
const inputRef = useRef(null);
const typingTimeout = useRef(null);
const currentAdminId = adminInfo?._id ?? "";
const bgColor = useColorModeValue('white', 'gray.700');
const POINT_VALUE = 50000;

const invoiceId = useMemo(() => {
    const now = new Date();
    const formattedDate = `${now.getFullYear().toString().slice(2)}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
    return `${adminInfo?.shop_code ?? ''}-EBS${formattedDate}0000`;
}, [adminInfo]);

const [errors, setErrors] = useState({});


const [formData, setFormData] = useState({
    adminId: currentAdminId,
    userId: "",
    invoiceId: invoiceId,
    amount: 0,
    points: 0
});

const remainder = formData.amount % POINT_VALUE;
const remaining =
  remainder === 0 ? POINT_VALUE : POINT_VALUE - remainder;

useEffect(() => {
    setFormData(prev => ({ ...prev, adminId: currentAdminId, invoiceId }));
}, [currentAdminId, invoiceId]);

const updateUserData = useCallback(async (userId) => {
  try {
    const userResponse = await getUserData(userId).unwrap();
    setUserData(userResponse.user);
  } catch (err) {
    error(err.data?.message || 'An error occurred while fetching user data')
  }
}, [getUserData, error]);    

const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({ ...prevData, [name]: value }));
    
    clearTimeout(typingTimeout.current);
    
    // Only set timeout and call updateUserData if the changed field is 'userId'
    if (name === 'userId') {
        typingTimeout.current = setTimeout(() => updateUserData(value), 1000);
    }
};

const handleQrCodeButton = () => {
    setQrScannerVisible(prev => !prev);
    if (inputRef.current) inputRef.current.focus();
};

const handleScan = (data) => {
    if (data) {
        const userId = data.text;
        setFormData(prev => ({ ...prev, userId }));
        updateUserData(userId);
        setQrScannerVisible(false);
        success("QR Code Scanned Successfully!");
    }
};

const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.userId || !formData.invoiceId || formData.amount <= 0 || formData.points <= 0) {
        setErrorMessage('Please fill in all required fields.');
        return;
    }
    try {
        const response = await addPointUser({ ...formData, adminId: currentAdminId }).unwrap();
        success(response.message);
        setFormData({ adminId: "", userId: "", invoiceId, amount: 0, points: 0 });
        updateUserData(formData.userId);
    } catch (err) {
        error(err.data?.message || 'An unexpected error occurred.');
    }
};

// const handleAmountChange = (valStr, valNum) => {
//   if (!valNum || valNum < POINT_VALUE) {
//     setFormData((prev) => ({ ...prev, amount: valNum || 0, points: 0 }));
//     setErrors({
//       amount: "Minimum amount is 50,000 for 1 point",
//     });
//     return;
//   }

//   if (valNum % POINT_VALUE !== 0) {
//     setFormData((prev) => ({ ...prev, amount: valNum }));
//     setErrors({
//       amount: "Amount must be in multiples of 50,000",
//     });
//     return;
//   }

//   const calculatedPoints = Math.floor(valNum / POINT_VALUE);

//   setErrors({});
//   setFormData((prev) => ({
//     ...prev,
//     amount: valNum,
//     points: calculatedPoints,
//   }));
// };

const handleAmountChange = (valStr, valNum) => {
  const amount = valNum || 0;

  // Below minimum
  if (amount > 0 && amount < POINT_VALUE) {
    setErrors({
      amount: "Minimum amount is 50,000 for 1 point",
    });

    setFormData((prev) => ({
      ...prev,
      amount,
      points: 0,
    }));
    return;
  }

  // Valid amount
  const calculatedPoints = Math.floor(amount / POINT_VALUE);

  setErrors({});
  setFormData((prev) => ({
    ...prev,
    amount,
    points: calculatedPoints,
  }));
};


const handlePointsChange = (valStr, valNum) => {
  if (!valNum || valNum < 1) return;

  const requiredAmount = valNum * POINT_VALUE;

  if (formData.amount < requiredAmount) {
    setErrors({
      points: `Minimum amount for ${valNum} point(s) is ${requiredAmount.toLocaleString()}`,
    });
    return;
  }

  setErrors({});
  setFormData((prev) => ({
    ...prev,
    points: valNum,
  }));
};


// const handlePointsChange = (valStr, valNum) => {
//   if (!valNum || valNum < 1) return;

//   const requiredAmount = valNum * POINT_VALUE;

//   if (formData.amount !== requiredAmount) {
//     setErrors({
//       points: `Amount must be ${requiredAmount.toLocaleString()} for ${valNum} point(s)`,
//     });
//     return;
//   }

//   setErrors({});
//   setFormData((prev) => ({
//     ...prev,
//     points: valNum,
//   }));
// };

return (
    <Stack 
      direction={{ base: 'column', md: 'row' }}
      spacing={8}
      align="stretch"
      width="100%"
      >
      {/* Left Panel - Form Section */}
      <Box 
        flex={{ base: 'none', md: 1 }}
        p={5}
        minWidth={{ base: '100%', md: '50%' }}
        >
        <Box 
          p={6}
          maxW={'450px'}
          w={'full'}
          borderRadius="xl"
          boxShadow={'xl'}
          bg={bgColor}
          borderWidth={'thin'}
          minH={isLoading ? "50vh" : "auto"}
          mx="auto"
          >
          <form onSubmit={handleSubmit}>
            {isLoading ? (
              <Center minH="300px">
                <Loader color={bgColor} />
              </Center>
            ) : (
              <VStack spacing={5} align="stretch">
                {/* Error Message */}
                {errorMessage && (
                  <Alert status="error" borderRadius="md" mb={4}>
                    <AlertIcon />
                    {errorMessage}
                  </Alert>
                )}

                {/* QR Scanner Toggle */}
                <Tooltip label={isQrScannerVisible ? "Close scanner" : "Scan QR code"}>
                  <IconButton
                    icon={isQrScannerVisible ? <FaTimes /> : <FaQrcode />}
                    colorScheme="blue"
                    onClick={handleQrCodeButton}
                    aria-label="Toggle QR Scanner"
                    alignSelf="flex-end"
                  />
                </Tooltip>
                
                {/* QR Scanner */}
                {isQrScannerVisible && (
                  <Box mt={4} borderWidth={1} borderRadius="md" p={4}>
                    <QrScanner 
                      delay={300} 
                      onError={() => setErrorMessage("Failed to scan. Try again.")} 
                      onScan={handleScan}
                      style={{ width: '100%', borderRadius: '0.375rem' }}
                    />
                  </Box>
                )}

                {/* Form Fields */}
                <FormControl isRequired>
                  <FormLabel>User ID</FormLabel>
                  <Input 
                    name="userId" 
                    placeholder="Enter User ID" 
                    value={formData.userId} 
                    onChange={handleChange} 
                    ref={inputRef} 
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Invoice ID</FormLabel>
                  <Input 
                    name="invoiceId" 
                    placeholder="Enter Invoice ID" 
                    value={formData.invoiceId} 
                    onChange={handleChange} 
                  />
                </FormControl>

                <FormControl isRequired isInvalid={!!errors.amount}>
                  <FormLabel>Amount</FormLabel>
                  <NumberInput
                    min={0}
                    value={formData.amount}
                    onChange={handleAmountChange}
                  >
                    <NumberInputField />
                  </NumberInput>
                   {!errors.amount && formData.amount >= POINT_VALUE && remainder !== 0 && (
                      <FormHelperText color="gray.500">
                        Spend <b>{remaining.toLocaleString()}</b> more to earn 1 more point
                      </FormHelperText>
                    )}
                  <FormErrorMessage>{errors.amount}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={!!errors.points}>
                  <FormLabel>Points</FormLabel>
                  <NumberInput
                    min={1}
                    max={99}
                    value={formData.points}
                    onChange={handlePointsChange}
                    // isReadOnly
                  >
                    <NumberInputField />
                  </NumberInput>
                  <FormErrorMessage>{errors.points}</FormErrorMessage>
                </FormControl>


                {/* <FormControl isRequired>
                  <FormLabel>Amount</FormLabel>
                  <NumberInput 
                    min={0} 
                    value={formData.amount} 
                    onChange={(valStr, valNum) => setFormData(prev => ({ ...prev, amount: valNum }))}
                  >
                    <NumberInputField name="amount" />
                  </NumberInput>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Points</FormLabel>
                  <NumberInput 
                    min={1} 
                    max={99} 
                    value={formData.points} 
                    onChange={(valStr, valNum) => setFormData(prev => ({ ...prev, points: valNum }))}
                  >
                    <NumberInputField name="points" />
                  </NumberInput>
                </FormControl> */}

                {/* Action Buttons */}
                <ButtonGroup spacing={4} mt={4}>
                  <Button 
                    type="submit" 
                    colorScheme="teal" 
                    flex={1}
                    isLoading={isLoading}
                    isDisabled={
                      !formData.userId || 
                      !formData.invoiceId || 
                      formData.amount <= 0 || 
                      formData.points <= 0
                    }
                  >
                    Add Point
                  </Button>
                  <Button 
                    colorScheme="gray" 
                    onClick={() => setFormData({ 
                      adminId: "", 
                      userId: "", 
                      invoiceId: "", 
                      amount: 0, 
                      points: 0 
                    })}
                  >
                    Clear
                  </Button>
                </ButtonGroup>
              </VStack>
            )}
          </form>
        </Box>
      </Box>

        {/* Right Panel - User Preview */}
        <Box 
        flex={{ base: 'none', md: 1 }}
        p={5}
        minWidth={{ base: '100%', md: '50%' }}
        >
          <Box 
            maxW={'320px'} 
            w={'full'} 
            bg={bgColor}
            boxShadow={'xl'} 
            rounded={'lg'} 
            p={6} 
            textAlign={'center'}
            borderWidth={'thin'}
            >
            {userData ? (
            <>
              <Avatar 
                size={'xl'} 
                src={userData?.image_url || '/default-user.png'} 
                mb={4}
                name={userData?.name}
              />
              <Heading fontSize={'2xl'}>{userData?.name || 'User'}</Heading>
              <Text fontWeight={600} color={'gray.500'} mb={4}>
                {userData?.email || 'Email not available'}
              </Text>
              <Badge 
                px={2} 
                py={1} 
                fontSize={'1.5rem'} 
                fontWeight={'800'}
                colorScheme="green"
                >
                {userData?.current_points || '0'} Points
              </Badge>
              <Badge 
                px={3} 
                py={1} 
                fontSize={'md'} 
                colorScheme="blue"
                borderRadius="full"
                >
                Member since {new Date(userData.createdAt).toLocaleDateString()}
              </Badge>
              </>
              ) : (
                <VStack spacing={4}>
                  <Avatar size={'xl'} src="/default-user.png" mb={4} />
                  <Heading fontSize={'xl'}>No user selected</Heading>
                  <Text color={'gray.500'}>
                    {isQrScannerVisible 
                      ? "Scan a QR code or enter User ID" 
                      : "Enter a User ID to view details"}
                  </Text>
                </VStack>
              )}
          </Box>
        </Box>
    </Stack>
  );
};

export default AdminAddPoints;